import BaseResource from './index'
import { EventEmitter } from 'events'
import { env } from '@/config'

// One physical WebSocket per (socketPath, id), shared across every JS resource instance
// that wraps that id. Without this, every fetch (list/detail/related resolve) creates a
// brand new CalendarResource/RaceEventResource instance -- and before this pool existed,
// each of those instances opened and owned its own independent socket, so a screen like
// AdminRaceControl.vue (which loads many EventForm.vue instances, each re-resolving the
// same calendars) could end up with a dozen live sockets all subscribed to the same
// calendar/event id. Pooling collapses that back down to exactly one connection per id.
const pools = new Map()

function poolKey(socketPath, id) {
    return `${socketPath}:${id}`
}

function getPool(socketPath, id) {
    const key = poolKey(socketPath, id)
    let pool = pools.get(key)

    if (!pool) {
        pool = {
            socketPath,
            id,
            socket: null,
            refCount: 0,
            wantsOpen: false,
            pingIntervalId: null,
            reconnectTimeoutId: null,
            connecting: null,
            listeners: new Set()
        }

        pools.set(key, pool)
    }

    return pool
}

function socketBaseURLFor() {
    return env.API_BASE_URL.replace(/^http/, 'ws').replace(/api\./gi, 'sockets.').replace(8000, 3000)
}

function openPoolSocket(pool, onFirstOpen) {
    if (pool.reconnectTimeoutId) {
        clearTimeout(pool.reconnectTimeoutId)
        pool.reconnectTimeoutId = null
    }

    const socketURL = `${socketBaseURLFor()}/ws/launcher/${pool.socketPath}/${pool.id}/`
    const ws = new WebSocket(socketURL)
    pool.socket = ws

    ws.onmessage = (event) => {
        let value

        try {
            value = JSON.parse(event.data)
        } catch (e) {
            return
        }

        pool.listeners.forEach((listener) => listener('message', value))
    }

    ws.onopen = () => {
        pool.connecting = null

        // A single ping interval per pooled connection, always cleared before a new one is
        // made -- previously each resource instance ran its own setInterval() that was
        // never cleared on close, so after a reconnect (or after an "orphaned" instance was
        // simply dropped without being closed) those stale intervals kept firing .send() on
        // an already-closed socket forever, which is where the "WebSocket is already in
        // CLOSED state" errors came from.
        if (pool.pingIntervalId) {
            clearInterval(pool.pingIntervalId)
        }

        pool.pingIntervalId = setInterval(() => {
            if (pool.socket && pool.socket.readyState === WebSocket.OPEN) {
                pool.socket.send(JSON.stringify({ type: 'ping' }))
            }
        }, 3e4)

        pool.listeners.forEach((listener) => listener('open'))

        if (onFirstOpen) {
            onFirstOpen()
        }
    }

    ws.onclose = () => {
        if (pool.socket === ws) {
            pool.socket = null
        }

        if (pool.pingIntervalId) {
            clearInterval(pool.pingIntervalId)
            pool.pingIntervalId = null
        }

        pool.connecting = null
        pool.listeners.forEach((listener) => listener('close'))

        if (pool.wantsOpen) {
            pool.reconnectTimeoutId = setTimeout(() => {
                pool.reconnectTimeoutId = null
                pool.listeners.forEach((listener) => listener('retry'))
                pool.connecting = new Promise((resolve) => openPoolSocket(pool, resolve))
            }, 2000)
        }
    }
}

export default class SocketResource extends BaseResource {
    // Override in subclasses: the /ws/launcher/<socketPath>/:id/ segment.
    static socketPath = null

    _pool() {
        return getPool(this.constructor.socketPath, this.id)
    }

    get emitter() {
        if (!this._emitter) {
            this._emitter = new EventEmitter()
            this._emitter.setMaxListeners(0)
        }

        return this._emitter
    }

    get socket() {
        if (!this._socketAcquired) {
            this.socketConnect()
        }

        return this._pool().socket
    }

    get socketClosed() {
        return !this._pool().wantsOpen
    }

    socketConnect() {
        const pool = this._pool()

        if (!this._socketAcquired) {
            this._socketAcquired = true
            this._poolListener = (type, payload) => this._onPoolEvent(type, payload)
            pool.listeners.add(this._poolListener)
            pool.refCount += 1
        }

        pool.wantsOpen = true

        if (pool.socket && pool.socket.readyState === WebSocket.OPEN) {
            // Connection already live from another instance sharing this id -- replay
            // 'open' on a microtask so a caller that attaches its listener immediately
            // after calling socketConnect() (the common pattern in this codebase) doesn't
            // miss it.
            Promise.resolve().then(() => this._onPoolEvent('open'))
            return Promise.resolve(this.emitter)
        }

        if (!pool.connecting) {
            pool.connecting = new Promise((resolve) => openPoolSocket(pool, resolve))
        }

        return pool.connecting.then(() => this.emitter)
    }

    socketWatchForChanges() {
        this.socketConnect()
        return this.emitter
    }

    _onPoolEvent(type, payload) {
        if (type === 'open') {
            this.emitter.emit('open')
            return
        }

        if (type === 'close') {
            this.emitter.emit('close')
            return
        }

        if (type === 'retry') {
            this.emitter.emit('retry')
            return
        }

        if (!['ping', 'pong'].includes(payload.type)) {
            console.log(`Received new ${this.constructor.socketLabel || this.constructor.socketPath} data`, payload)
        }

        if (payload.type === 'model_updated') {
            Object.assign(this.attributes, payload.data)

            // Mirror BaseResource's constructor, which flattens readonly_values onto
            // attributes -- without this, fields only exposed via readonly_values (e.g.
            // Calendar's "event") would go stale after a live update even though the raw
            // attributes.readonly_values.* value is current.
            if (payload.data && payload.data.readonly_values) {
                Object.assign(this.attributes, payload.data.readonly_values)
            }

            this.changes = {}
            this.emitter.emit('update', payload.data)
        } else if (payload.type === 'peers') {
            if (payload.value && payload.value.type === 'server') {
                this.emitter.emit('server', payload.value.value)
            }

            this.emitter.emit('peers', payload.value)
        }

        this.emitter.emit('message', payload)
    }

    _send(payload) {
        const socket = this.socket

        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload))
        }
    }

    socketSendSignal(signal) {
        this._send({ type: 'signal', value: signal })
    }

    socketSendPeers(value) {
        this._send({ type: 'peers', value })
    }

    socketClose() {
        if (!this._socketAcquired) {
            return
        }

        this._socketAcquired = false
        const pool = this._pool()
        pool.listeners.delete(this._poolListener)
        pool.refCount = Math.max(0, pool.refCount - 1)

        if (pool.refCount > 0) {
            return
        }

        pool.wantsOpen = false

        if (pool.reconnectTimeoutId) {
            clearTimeout(pool.reconnectTimeoutId)
            pool.reconnectTimeoutId = null
        }

        if (pool.pingIntervalId) {
            clearInterval(pool.pingIntervalId)
            pool.pingIntervalId = null
        }

        if (pool.socket) {
            const socketToClose = pool.socket
            pool.socket = null
            socketToClose.onclose = null
            socketToClose.onmessage = null
            socketToClose.close()
        }

        pools.delete(poolKey(pool.socketPath, pool.id))
    }
}
